/**
 * Centralized axios client for the Strata x Framework frontend.
 *
 * Phase A1 — Proof of concept for the new `src/api/` architecture
 * described in `/app/memory/FRONTEND_ARCHITECTURE_AUDIT.md`. Only
 * Client Reports endpoints consume this in Phase A1; other features
 * migrate one domain at a time.
 *
 * What this client does for you:
 *   • baseURL automatically set from REACT_APP_BACKEND_URL + '/api'
 *   • withCredentials: true on every call (cookie-based session)
 *   • JSON content-type defaults
 *   • Errors are thrown as Axios errors UNTOUCHED so existing
 *     consumers reading `err.response?.status` keep working.
 *   • Safe retry on transient network failures for GET only
 *     (see RETRY POLICY below).
 *
 * NOT in this client (deliberately, to keep the layer small):
 *   • Refresh-on-401 (deferred to a later phase)
 *   • Global toast on 5xx (callers still own UX messaging)
 *   • Request cancellation / debouncing
 *   • Cache integration with `lib/apiCache.js`
 *
 * Usage pattern in `src/api/<domain>.js`:
 *
 *     import { apiClient } from './client';
 *     export const getThing = (id) => apiClient.get(`/things/${id}`);
 *
 * Consumers never import axios directly — they import typed
 * functions from `src/api/<domain>.js`.
 *
 * ---
 * RETRY POLICY (Phase A1 follow-up, 2026-06-13)
 *
 * We retry **only** when ALL of the following are true:
 *   • The request method is GET (idempotent — POST/PUT/PATCH/DELETE
 *     are NEVER retried; a re-issued POST could double-create a
 *     payment, a re-issued DELETE could fail loudly the second
 *     time and confuse the caller).
 *   • No response was received (`err.response` is undefined) —
 *     i.e. true network failure, DNS miss, TCP reset, timeout,
 *     proxy 502 before-response. HTTP 4xx and 5xx WITH a response
 *     body are NOT retried (the server intentionally said no).
 *   • The error is not an aborted request (`err.code === 'ERR_CANCELED'`
 *     / axios cancelation). User-initiated aborts must not
 *     resurrect.
 *   • We haven't already burned through 3 attempts.
 *
 * Backoff schedule: 100 ms, 300 ms, 900 ms. The final error
 * reaches the caller UNCHANGED so existing `err.response?.status`
 * / `extractMessage(err)` reads keep working.
 *
 * Behaviour is entirely contained to this file. Callers and tests
 * for `api/reports.js` need zero changes.
 */
import axios from 'axios';
import { getAccessToken, getActiveTenant } from '@/lib/authToken';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

if (!BACKEND_URL && process.env.NODE_ENV !== 'test') {
  // eslint-disable-next-line no-console
  console.warn(
    '[api/client] REACT_APP_BACKEND_URL is not set — API calls will fail.',
  );
}

export const apiClient = axios.create({
  baseURL: `${BACKEND_URL || ''}/api`,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Escape-hatch: callers that need to talk to a non-/api path on the
 * same backend (e.g. a presigned download) can use this. Same auth
 * + credentials behaviour, but no /api prefix.
 */
export const apiClientNoPrefix = axios.create({
  baseURL: BACKEND_URL || '',
  withCredentials: true,
});

// --- Retry policy --- //

const RETRY_BACKOFFS_MS = [100, 300, 900];

// Allow tests to override the sleep so they don't wait 1.3s on
// every failure case. Default uses real setTimeout.
let _sleep = (ms) => new Promise((r) => setTimeout(r, ms));
export function __setRetrySleepForTests(fn) {
  _sleep = fn;
}

function shouldRetry(err) {
  // User-initiated cancellation — bail immediately.
  if (axios.isCancel?.(err)) return false;
  if (err?.code === 'ERR_CANCELED') return false;
  if (err?.name === 'CanceledError') return false;

  // Only idempotent GET requests are eligible.
  const method = (err?.config?.method || '').toLowerCase();
  if (method !== 'get') return false;

  // If the server responded (any HTTP status), trust it — don't retry.
  // This covers 4xx auth/validation errors AND 5xx that DID reach the
  // server. Only retry when no response was received at all.
  if (err?.response) return false;

  return true;
}

// Track how many times we've retried THIS request. Stored on the
// axios config so concurrent requests don't share counters.
function getAttemptCount(config) {
  return config.__retryAttempt || 0;
}

// iter-156sf — attach the Bearer access token (Safari cross-site cookie
// fallback) on every request. Harmless on Chrome where the cookie wins.
function attachBearer(config) {
  const t = getAccessToken();
  config.headers = config.headers || {};
  if (t && !config.headers.Authorization) config.headers.Authorization = `Bearer ${t}`;
  const tenant = getActiveTenant();
  if (tenant && !config.headers['X-Active-Tenant']) config.headers['X-Active-Tenant'] = tenant;
  return config;
}
// The JSON default would make axios serialize a FormData body to JSON (the file is lost → 422); drop it so the
// browser sends multipart/form-data with its boundary.
function formDataContentType(config) {
  if (typeof FormData !== 'undefined' && config.data instanceof FormData) config.headers.delete('Content-Type');
  return config;
}
apiClient.interceptors.request.use(attachBearer);
apiClientNoPrefix.interceptors.request.use(attachBearer);
apiClient.interceptors.request.use(formDataContentType);
apiClientNoPrefix.interceptors.request.use(formDataContentType);

// iter-156sf-step2 — Refresh-on-401 for the centralized client. The
// report form + photo upload run through `apiClient`, which had NO
// refresh interceptor, so a 60-min access-token expiry mid-walk
// bounced the field tech to login. Installing the shared single-flight
// interceptor (same lock as the global axios instance) makes these
// flows transparently re-auth while the user is active. Installed
// AFTER the retry interceptor below so a 401 (which always has a
// response → not retried) falls straight through to the refresh path.
import { installRefreshInterceptor } from '@/lib/authRefresh';
installRefreshInterceptor(apiClient);
installRefreshInterceptor(apiClientNoPrefix);

apiClient.interceptors.response.use(
  (response) => response,
  async (err) => {
    if (!err?.config) {
      // No config attached (programmer error / aborted before send)
      throw err;
    }
    const attempt = getAttemptCount(err.config);
    if (!shouldRetry(err) || attempt >= RETRY_BACKOFFS_MS.length) {
      throw err;
    }
    const delay = RETRY_BACKOFFS_MS[attempt];
    err.config.__retryAttempt = attempt + 1;
    await _sleep(delay);
    return apiClient.request(err.config);
  },
);
